import { services } from '../data';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  ChevronRight, 
  Check, 
  ArrowRight, 
  ChevronDown, 
  Shield, 
  Clock, 
  Award, 
  Zap, 
  X, 
  Plus,
  Sparkles,
  Play,
  Calendar,
  Mail,
  Phone,
  Wrench,
  Wind,
  ThermometerSun,
  SquareSigma,
  BrickWall,
  Blinds,
  DraftingCompass,
  ConstructionIcon,
  Trees,
  GlassWater,
  Home,
  ShieldCheck,
  MapPin,
  GalleryThumbnails,
  InspectionPanel,
  GalleryVertical,
  Fence
} from 'lucide-react';

function Services() {
  const location = useLocation();
  const navigate = useNavigate();
  const [viewingService, setViewingService] = useState(null);
  const [activeSubService, setActiveSubService] = useState(null);
  const subServiceDetailsRef = useRef(null);
  const servicesSectionRef = useRef(null);

  // Set initial service with error handling
  useEffect(() => {
    
    const serviceId = location.state?.serviceId?.toString(); // Ensure string type
  

    try {
      // Check if services is valid
      if (!services || !Array.isArray(services) || services.length === 0) {
       
        setViewingService({
          id: 'fallback',
          title: 'Default Service',
          description: 'No services available at the moment.',
          subServices: []
        });
        return;
      }

      // Find selected service or default to first service
      const selectedService = serviceId 
        ? services.find((service) => service.id.toString() === serviceId) 
        : null;
      const initialService = selectedService || services[0];
      
    
      if (initialService) {
        setViewingService(initialService);
      } else {
     
        setViewingService({
          id: 'fallback',
          title: 'Default Service',
          description: 'No services available.',
          subServices: []
        });
      }
    } catch (error) {
      
      setViewingService({
        id: 'fallback',
        title: 'Default Service',
        description: 'An error occurred while loading services.',
        subServices: []
      });
    }
  }, [location.state]); // Depend on location.state

  // Handle auto-scroll to services section
  useEffect(() => {
    const serviceId = location.state?.serviceId?.toString();
    
    if (serviceId && servicesSectionRef.current) {
     
      const scrollToSection = () => {
        // Reset scroll to top to ensure downward motion
        window.scrollTo({ top: 0, behavior: 'instant' });
       

        if (servicesSectionRef.current) {
          const rect = servicesSectionRef.current.getBoundingClientRect();
          const offsetTop = window.pageYOffset + rect.top;
          const headerOffset = 80; // Adjust based on your header height
          const scrollPosition = offsetTop - headerOffset;

          window.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });
        
        } else {
          
        }
      };

      // Delay scroll to ensure DOM is fully rendered
      const timer = setTimeout(scrollToSection, 1000);
      return () => clearTimeout(timer);
    }
  }, [location.state, viewingService]); // Depend on location.state and viewingService

  // Auto-scroll to expanded sub-service details
  useEffect(() => {
    if (activeSubService && subServiceDetailsRef.current) {
      setTimeout(() => {
        subServiceDetailsRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 500);
    }
  }, [activeSubService]);

  const handleViewService = (service) => {
  
    setViewingService(service);
    setActiveSubService(null);
    navigate('/services', { state: { serviceId: service.id.toString() } });
  };

  const benefitItems = [
    {
      icon: <Shield className="h-10 w-10 text-sky-blue" />,
      title: "Certified Quality",
      description: "All our engineering services meet rigorous industry standards and certifications."
    },
    {
      icon: <Clock className="h-10 w-10 text-sky-blue" />,
      title: "Rapid Turnaround",
      description: "Efficient processes ensure your projects stay on schedule and within budget."
    },
    {
      icon: <Award className="h-10 w-10 text-sky-blue" />,
      title: "Expert Team",
      description: "Our engineers bring decades of combined experience to every project."
    },
    {
      icon: <Zap className="h-10 w-10 text-sky-blue" />,
      title: "Innovative Solutions",
      description: "We employ cutting-edge technology and methods for optimal results."
    }
  ];

  // Assign icons to sub-services based on their ID and context
  const getSubServiceIcon = (subService) => {
    const id = subService.id;

    // Commercial Sector: Façade Engineering (ID 701–707)
    switch (id) {
      case 701: // Certified Wind Load Analysis
        return <Wind className="h-12 w-12 text-sky-blue" />;
      case 702: // Thermal Analysis
        return <ThermometerSun className="h-12 w-12 text-sky-blue" />;
      case 703: // Glazing Structural Calculations
        return <SquareSigma className="h-12 w-12 text-sky-blue" />;
      case 704: // Glazing Support Structures Design
        return <Wrench className="h-12 w-12 text-sky-blue" />;
      case 705: // Cladding Systems Engineering
        return <BrickWall className="h-12 w-12 text-sky-blue" />;
      case 706: // Metal Louvers Design
        return <Blinds className="h-12 w-12 text-sky-blue" />;
      case 707: // Glazing & General Shop Drawings
        return <DraftingCompass className="h-12 w-12 text-sky-blue" />;
    }

    // Commercial Sector: Steel & Aluminum Structures (ID 801–807)
    switch (id) {
      case 801: // Metal Staircases & Guardrails
        return <ConstructionIcon className="h-12 w-12 text-sky-blue" />;
      case 802: // Elevator Hoist & Lifeline Beams
        return <Wrench className="h-12 w-12 text-sky-blue" />;
      case 803: // Fencing, Pergolas & Canopies
        return <Fence  className="h-12 w-12 text-sky-blue" />;
      case 804: // Glass Railing Systems - Frameless
      return <GalleryThumbnails className="h-12 w-12 text-sky-blue" />;
      case 805: // Glass Railing Systems - Point Supported
      return <InspectionPanel  className="h-12 w-12 text-sky-blue" />;
      case 806: // Glass Railing Systems - Infill
        return <GalleryVertical className="h-12 w-12 text-sky-blue" />;
      case 807: // General Shop Drawings
        return <DraftingCompass className="h-12 w-12 text-sky-blue" />;
    }

    // Residential Sector (ID 901–905)
    switch (id) {
      case 901: // Certified Wind Load Analysis
        return <Wind className="h-12 w-12 text-sky-blue" />;
      case 902: // Residential Glazing Permit Packages
        return <Home className="h-12 w-12 text-sky-blue" />;
      case 903: // Glazing & Support Structures Analysis
        return <Wrench className="h-12 w-12 text-sky-blue" />;
      case 904: // Special Inspector Services
        return <ShieldCheck className="h-12 w-12 text-sky-blue" />;
      case 905: // Site-Specific Engineering
        return <MapPin className="h-12 w-12 text-sky-blue" />;
    }

    // Fallback icon
    return <Wrench className="h-12 w-12 text-sky-blue" />;
  };

  if (!viewingService) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="h-12 w-12 border-4 border-t-sky-blue border-gray-200 rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-gray-100 to-white min-h-screen">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-dark-blue text-white">
        <div className="absolute inset-0 bg-gradient-to-r from-sky-900/60 to-transparent"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-80" 
          style={{ backgroundImage: `url('/assets/images/ESW/esw-westwharf-1.jpg')` }}
        ></div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="container mx-auto px-4 py-24 relative z-20 mt-20"
        >
          <div className="flex flex-col items-center text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="h-20 w-20 bg-sky-500 rounded-full flex items-center justify-center mb-6"
            >
              <motion.div 
                animate={{ rotate: 360 }} 
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="h-12 w-12 bg-white/20 rounded-full"
              ></motion.div>
            </motion.div>

            <motion.span 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="inline-block px-4 py-1 bg-sky-blue/30 text-white font-medium rounded-full mb-4"
            >
              Engineering Excellence
            </motion.span>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
            >
              The <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-cyan-300">Facade Connection</span> Services
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)' }}
              className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto"
            >
              Delivering precision-engineered solutions for the most demanding projects. Our innovative approach combines technical expertise with practical implementation for exceptional results.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-10"
            >
              <a
                href="#services-section"
                className="bg-gradient-to-r from-sky-500 to-sky-500 text-white py-4 px-8 rounded-full hover:bg-slate-500 transition duration-300 font-medium text-lg inline-flex items-center"
              >
                Explore Our Services <ChevronDown className="ml-2 h-5 w-5" />
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Benefits Section */}
      <motion.div 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
        className="container mx-auto px-4 py-20"
      >
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-dark-blue mb-4">Why Choose Our Services</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            We bring unparalleled expertise and innovation to every project, ensuring optimal results that exceed expectations.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefitItems.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
              className="bg-white p-8 rounded-xl shadow-md transition duration-300 text-center border border-gray-100"
            >
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 rounded-full bg-sky-blue/10 flex items-center justify-center">
                  {benefit.icon}
                </div>
              </div>
              <h3 className="text-xl font-semibold text-dark-blue mb-3">{benefit.title}</h3>
              <p className="text-gray-600">{benefit.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Sub-Services Detail View */}
      <motion.div
        id="services-section"
        ref={servicesSectionRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="py-16 bg-gray-50"
      >
        <div className="container mx-auto px-4">
          {/* Service Selector */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-12"
          >
            <h3 className="text-2xl font-bold text-dark-blue mb-6">Our Services</h3>
            <div className="flex flex-wrap gap-4">
              {services.map((service, index) => (
                <motion.button
                  key={service.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.05, boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleViewService(service)}
                  className={`flex items-center px-6 py-3 rounded-full font-medium text-sm transition-all duration-300 ${
                    viewingService?.id.toString() === service.id.toString()
                      ? 'bg-gradient-to-r from-sky-blue to-dark-blue text-white'
                      : 'bg-white text-dark-blue border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <img
                    src={service.icon}
                    alt={service.title}
                    className="h-6 w-6 mr-2 object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/assets/icons/default-service.svg";
                    }}
                  />
                  {service.title}
                </motion.button>
              ))}
            </div>
          </motion.div>

          <motion.div 
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-dark-blue bg-gradient-to-r from-dark-blue to-sky-blue bg-clip-text text-transparent">
                  {viewingService.title}
                </h2>
                <p className="text-gray-600 mt-2 max-w-2xl">{viewingService.description}</p>
              </div>
              
              <a
                href="/contact"
                className="bg-gradient-to-r from-sky-blue to-dark-blue text-white py-3 px-6 rounded-full hover:from-dark-blue hover:to-sky-blue transition flex items-center justify-center whitespace-nowrap shadow-lg"
              >
                Request a Quote <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </motion.div>
          
          <div className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {viewingService.subServices.map((subService, index) => (
                <motion.div
                  key={subService.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -10, scale: 1.02, boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)" }}
                  className={`relative bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 border-2 ${
                    activeSubService === subService.id 
                      ? 'border-sky-blue' 
                      : 'border-transparent hover:border-sky-blue/20'
                  }`}
                >
                  <div className="p-6 text-center">
                    <div className="flex justify-center mb-4">
                      {getSubServiceIcon(subService)}
                    </div>
                    <h3 className="text-xl font-bold text-dark-blue mb-3">{subService.title}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">{subService.description}</p>
                    <motion.button
                      onClick={() => setActiveSubService(activeSubService === subService.id ? null : subService.id)}
                      className="bg-gradient-to-r from-sky-blue to-dark-blue text-white px-6 py-2 rounded-full hover:from-dark-blue hover:to-sky-blue transition font-medium"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Read More
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Expanded Sub-Service Details */}
          <AnimatePresence>
            {activeSubService && viewingService.subServices.find(s => s.id === activeSubService) && (
              <motion.div
                ref={subServiceDetailsRef}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl overflow-hidden mb-16"
              >
                {(() => {
                  const subService = viewingService.subServices.find(s => s.id === activeSubService);
                  return (
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                      <div className="p-8 lg:p-10">
                        <div className="flex justify-between items-center mb-6">
                          <h3 className="text-2xl font-bold text-dark-blue">{subService.title}</h3>
                          <motion.button 
                            onClick={() => setActiveSubService(null)}
                            className="bg-gray-100 rounded-full p-2 hover:bg-gray-200 transition"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <X className="h-5 w-5 text-gray-600" />
                          </motion.button>
                        </div>
                        
                        {/* <div className="mb-6 flex justify-center items-center">
                          <img 
                            src={subService.image || '/assets/images/default-subservice.jpg'} 
                            alt={subService.title}
                            className="w-56 h-78 object-cover rounded-lg "
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/assets/images/default-subservice.jpg";
                            }}
                          />
                        </div>
                         */}
                        <p className="text-gray-700 mb-8 leading-relaxed">
                          {subService.description}
                        </p>
                        
                        <div className="space-y-8">
                          <div>
                            <h4 className="text-lg font-semibold text-dark-blue mb-4 flex items-center">
                              <Award className="h-5 w-5 mr-2 text-sky-blue" /> 
                              Key Benefits
                            </h4>
                            <ul className="space-y-3">
                              {subService.benefits.map((benefit, i) => (
                                <motion.li 
                                  key={i}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: i * 0.1, duration: 0.3 }}
                                  className="flex items-start"
                                >
                                  <div className="bg-sky-blue/10 p-1 rounded-full mr-3 flex-shrink-0">
                                    <Check className="h-4 w-4 text-sky-blue" />
                                  </div>
                                  <span className="text-gray-700">{benefit}</span>
                                </motion.li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <h4 className="text-lg font-semibold text-dark-blue mb-4 flex items-center">
                              <Sparkles className="h-5 w-5 mr-2 text-sky-blue" /> 
                              Our Process
                            </h4>
                            <ul className="space-y-4">
                              {subService.process.map((step, i) => (
                                <motion.li 
                                  key={i}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: i * 0.15, duration: 0.3 }}
                                  className="flex items-start"
                                >
                                  <div className="bg-gradient-to-r from-sky-blue to-dark-blue text-white rounded-full h-6 w-6 flex items-center justify-center mr-3 flex-shrink-0 font-medium text-sm">
                                    {i + 1}
                                  </div>
                                  <span className="text-gray-700">{step}</span>
                                </motion.li>
                              ))}
                            </ul>
                          </div>
                          
                      
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 p-8 lg:p-10 flex flex-col">
                        <div className="mb-6">
                          <h4 className="text-lg font-semibold text-dark-blue mb-4">Quick Information</h4>
                          <div className="bg-white p-6 rounded-xl shadow-sm space-y-4">
                            <div className="flex items-center">
                              <Clock className="h-5 w-5 text-sky-blue mr-3" />
                              <div>
                                <span className="block text-sm text-gray-500">Average Turnaround</span>
                                <span className="text-gray-800 font-medium">2-3 Weeks</span>
                              </div>
                            </div>
                            
                            <div className="flex items-center">
                              <Calendar className="h-5 w-5 text-sky-blue mr-3" />
                              <div>
                                <span className="block text-sm text-gray-500">Project Duration</span>
                                <span className="text-gray-800 font-medium">Depends on Scope</span>
                              </div>
                            </div>
                            
                            <div className="flex items-center">
                              <Award className="h-5 w-5 text-sky-blue mr-3" />
                              <div>
                                <span className="block text-sm text-gray-500">Certifications</span>
                                <span className="text-gray-800 font-medium">Industry Standard Compliant</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mb-8">
                          <h4 className="text-lg font-semibold text-dark-blue mb-4">Contact Specialist</h4>
                          <div className="bg-white p-6 rounded-xl shadow-sm">
                            <div className="flex items-start mb-4">
                              <Mail className="h-5 w-5 text-sky-blue mr-3 mt-0.5" />
                              <a href="mailto:specialist@structuralsolutions.com" className="text-gray-700 hover:text-sky-blue transition">
                               info@facadeconnection.com
                              </a>
                            </div>
                            <div className="flex items-start">
                              <Phone className="h-5 w-5 text-sky-blue mr-3 mt-0.5" />
                              <a href="tel:+15551234567" className="text-gray-700 hover:text-sky-blue transition">
                                (305) 528-1054
                              </a>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-auto">
                          <div className="bg-gradient-to-r from-dark-blue to-sky-blue rounded-xl p-6 text-white">
                            <h4 className="text-lg font-semibold flex items-center mb-3">
                              <Play className="h-5 w-5 mr-2 text-white" />
                              How It Works
                            </h4>
                            <p className="text-gray-200 mb-4">
                              Schedule a no-obligation consultation to discuss your project requirements and receive a customized solution.
                            </p>
                            <a
                              href="/contact"
                              className="inline-block w-full text-center bg-white text-dark-blue py-3 px-6 rounded-lg font-medium hover:bg-gray-100 transition shadow-lg"
                            >
                              Book a Consultation
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="bg-gradient-to-r from-dark-blue to-sky-blue rounded-2xl p-8 md:p-12 text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
            <div className="relative z-10 text-center md:text-left md:flex md:items-center md:justify-between">
              <div className="mb-6 md:mb-0">
                <h3 className="text-2xl md:text-3xl font-bold mb-3">Ready to Start Your Project?</h3>
                <p className="text-gray-200 max-w-xl">
                  Our team of expert engineers is ready to help you bring your vision to life with precision and excellence.
                </p>
              </div>
              <div>
                <a
                  href="/contact"
                  className="inline-flex items-center bg-white text-dark-blue px-8 py-4 rounded-full font-medium hover:bg-gray-100 transition shadow-lg"
                >
                  Schedule a Consultation <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}

export default Services;


// import { services } from '../data';          

// import { useState, useEffect, useRef } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import { useLocation, useNavigate } from 'react-router-dom';
// import { 
//   ChevronRight, 
//   Check, 
//   ArrowRight, 
//   ChevronDown, 
//   Shield, 
//   Clock, 
//   Award, 
//   Zap, 
//   X, 
//   Plus,
//   Sparkles,
//   Play,
//   Calendar,
//   Mail,
//   Phone,
//   Wrench,
//   Wind,
//   ThermometerSun,
//   SquareSigma,
//   BrickWall,
//   Blinds,
//   DraftingCompass,
//   ConstructionIcon,
//   Trees,
//   GlassWater,
//   Home,
//   ShieldCheck,
//   MapPin
// } from 'lucide-react';

// function Services() {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const [viewingService, setViewingService] = useState(null);
//   const [activeSubService, setActiveSubService] = useState(null);
//   const subServiceDetailsRef = useRef(null);
//   const servicesSectionRef = useRef(null);

//   // Set initial service with error handling
//   useEffect(() => {
//     console.log('Services data:', services);
//     const serviceId = location.state?.serviceId?.toString();
//     console.log('Service ID from state:', serviceId);
//     console.log('Location state:', location.state);

//     try {
//       if (!services || !Array.isArray(services) || services.length === 0) {
//         console.error('Services data is invalid or empty');
//         setViewingService({
//           id: 'fallback',
//           title: 'Default Service',
//           description: 'No services available at the moment.',
//           subServices: []
//         });
//         return;
//       }

//       const selectedService = serviceId 
//         ? services.find((service) => service.id.toString() === serviceId) 
//         : null;
//       const initialService = selectedService || services[0];
      
//       console.log('Selected service:', initialService);
//       if (initialService) {
//         setViewingService(initialService);
//       } else {
//         console.error('No initial service found');
//         setViewingService({
//           id: 'fallback',
//           title: 'Default Service',
//           description: 'No services available.',
//           subServices: []
//         });
//       }
//     } catch (error) {
//       console.error('Error setting initial service:', error);
//       setViewingService({
//         id: 'fallback',
//         title: 'Default Service',
//         description: 'An error occurred while loading services.',
//         subServices: []
//       });
//     }
//   }, [location.state]);

//   // Handle auto-scroll to services section
//   useEffect(() => {
//     const serviceId = location.state?.serviceId?.toString();
    
//     if (serviceId && servicesSectionRef.current) {
//       console.log('Attempting to scroll to services section');
//       const scrollToSection = () => {
//         window.scrollTo({ top: 0, behavior: 'instant' });
//         console.log('Reset scroll to top');

//         if (servicesSectionRef.current) {
//           const rect = servicesSectionRef.current.getBoundingClientRect();
//           const offsetTop = window.pageYOffset + rect.top;
//           const headerOffset = 80;
//           const scrollPosition = offsetTop - headerOffset;

//           window.scrollTo({
//             top: scrollPosition,
//             behavior: 'smooth'
//           });
//           console.log('Scrolling to position:', scrollPosition);
//         } else {
//           console.log('Services section ref not found');
//         }
//       };

//       const timer = setTimeout(scrollToSection, 1000);
//       return () => clearTimeout(timer);
//     }
//   }, [location.state, viewingService]);

//   // Auto-scroll to expanded sub-service details
//   useEffect(() => {
//     if (activeSubService && subServiceDetailsRef.current) {
//       setTimeout(() => {
//         subServiceDetailsRef.current.scrollIntoView({ 
//           behavior: 'smooth', 
//           block: 'start' 
//         });
//       }, 500);
//     }
//   }, [activeSubService]);

//   const handleViewService = (service) => {
//     console.log('Handling view service:', service);
//     setViewingService(service);
//     setActiveSubService(null);
//     navigate('/services', { state: { serviceId: service.id.toString() } });
//   };

//   const benefitItems = [
//     {
//       icon: <Shield className="h-8 w-8 text-sky-500" />,
//       title: "Certified Quality",
//       description: "All our engineering services meet rigorous industry standards and certifications."
//     },
//     {
//       icon: <Clock className="h-8 w-8 text-sky-500" />,
//       title: "Rapid Turnaround",
//       description: "Efficient processes ensure your projects stay on schedule and within budget."
//     },
//     {
//       icon: <Award className="h-8 w-8 text-sky-500" />,
//       title: "Expert Team",
//       description: "Our engineers bring decades of combined experience to every project."
//     },
//     {
//       icon: <Zap className="h-8 w-8 text-sky-500" />,
//       title: "Innovative Solutions",
//       description: "We employ cutting-edge technology and methods for optimal results."
//     }
//   ];

//   const getSubServiceIcon = (subService) => {
//     const id = subService.id;

//     switch (id) {
//       case 701:
//         return <Wind className="h-10 w-10 text-sky-500" />;
//       case 702:
//         return <ThermometerSun className="h-10 w-10 text-sky-500" />;
//       case 703:
//         return <SquareSigma className="h-10 w-10 text-sky-500" />;
//       case 704:
//         return <Wrench className="h-10 w-10 text-sky-500" />;
//       case 705:
//         return <BrickWall className="h-10 w-10 text-sky-500" />;
//       case 706:
//         return <Blinds className="h-10 w-10 text-sky-500" />;
//       case 707:
//         return <DraftingCompass className="h-10 w-10 text-sky-500" />;
//       case 801:
//         return <ConstructionIcon className="h-10 w-10 text-sky-500" />;
//       case 802:
//         return <Wrench className="h-10 w-10 text-sky-500" />;
//       case 803:
//         return <Trees className="h-10 w-10 text-sky-500" />;
//       case 804:
//       case 805:
//       case 806:
//         return <GlassWater className="h-10 w-10 text-sky-500" />;
//       case 807:
//         return <DraftingCompass className="h-10 w-10 text-sky-500" />;
//       case 901:
//         return <Wind className="h-10 w-10 text-sky-500" />;
//       case 902:
//         return <Home className="h-10 w-10 text-sky-500" />;
//       case 903:
//         return <Wrench className="h-10 w-10 text-sky-500" />;
//       case 904:
//         return <ShieldCheck className="h-10 w-10 text-sky-500" />;
//       case 905:
//         return <MapPin className="h-10 w-10 text-sky-500" />;
//       default:
//         return <Wrench className="h-10 w-10 text-sky-500" />;
//     }
//   };

//   if (!viewingService) {
//     return (
//       <div className="flex justify-center items-center min-h-screen bg-black">
//         <motion.div
//           animate={{ rotate: 360 }}
//           transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
//           className="h-12 w-12 border-4 border-t-sky-500 border-white/20 rounded-full"
//         />
//       </div>
//     );
//   }

//   return (
//     <div className="bg-black min-h-screen text-white font-sans">
//       {/* Hero Section (Unchanged) */}
//       <div className="relative overflow-hidden bg-dark-blue text-white">
//         <div className="absolute inset-0 bg-gradient-to-r from-sky-900/60 to-transparent"></div>
//         <div 
//           className="absolute inset-0 bg-cover bg-center opacity-80" 
//           style={{ backgroundImage: `url('/assets/images/ESW/esw-westwharf-1.jpg')` }}
//         ></div>
        
//         <motion.div 
//           initial={{ opacity: 0, y: 20 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.8 }}
//           className="container mx-auto px-4 py-24 relative z-20 mt-20"
//         >
//           <div className="flex flex-col items-center text-center">
//             <motion.div
//               initial={{ scale: 0 }}
//               animate={{ scale: 1 }}
//               transition={{ delay: 0.2, duration: 0.5 }}
//               className="h-20 w-20 bg-sky-500 rounded-full flex items-center justify-center mb-6"
//             >
//               <motion.div 
//                 animate={{ rotate: 360 }} 
//                 transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
//                 className="h-12 w-12 bg-white/20 rounded-full"
//               ></motion.div>
//             </motion.div>

//             <motion.span 
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ delay: 0.4, duration: 0.6 }}
//               className="inline-block px-4 py-1 bg-sky-blue/30 text-white font-medium rounded-full mb-4"
//             >
//               Engineering Excellence
//             </motion.span>
            
//             <motion.h1 
//               initial={{ opacity: 0, y: 30 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: 0.5, duration: 0.7 }}
//               className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
//             >
//               The <span className="text-sky-blue">Facade Connection</span> Services
//             </motion.h1>
            
//             <motion.p 
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ delay: 0.7, duration: 0.7 }}
//               className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto"
//             >
//               Delivering precision-engineered solutions for the most demanding projects. Our innovative approach combines technical expertise with practical implementation for exceptional results.
//             </motion.p>
            
//             <motion.div
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: 0.9, duration: 0.7 }}
//               className="mt-10"
//             >
//               <a
//                 href="#services-section"
//                 className="bg-sky-blue text-white py-4 px-8 rounded-full hover:bg-sky-blue/90 transition duration-300 font-medium text-lg inline-flex items-center"
//               >
//                 Explore Our Services <ChevronDown className="ml-2 h-5 w-5" />
//               </a>
//             </motion.div>
//           </div>
//         </motion.div>
//       </div>

//       {/* Benefits Section */}
//       <motion.div 
//         initial={{ opacity: 0 }}
//         whileInView={{ opacity: 1 }}
//         transition={{ duration: 0.8 }}
//         viewport={{ once: true }}
//         className="container mx-auto px-4 py-20"
//       >
//         <div className="text-center mb-12">
//           <motion.h2 
//             initial={{ y: 50, opacity: 0 }}
//             whileInView={{ y: 0, opacity: 1 }}
//             transition={{ duration: 0.6 }}
//             className="text-5xl font-extrabold text-white mb-4 tracking-tight"
//           >
//             Why Choose Us
//           </motion.h2>
//           <motion.p 
//             initial={{ y: 30, opacity: 0 }}
//             whileInView={{ y: 0, opacity: 1 }}
//             transition={{ duration: 0.6, delay: 0.2 }}
//             className="text-gray-300 max-w-2xl mx-auto text-lg"
//           >
//             Unmatched expertise and innovation for projects that demand excellence.
//           </motion.p>
//         </div>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
//           {benefitItems.map((benefit, index) => (
//             <motion.div
//               key={index}
//               initial={{ opacity: 0, y: 100, rotateX: -30 }}
//               whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
//               whileHover={{ 
//                 y: -10, 
//                 rotateX: 10, 
//                 rotateY: 10, 
//                 scale: 1.05,
//                 boxShadow: "0 15px 30px rgba(14, 165, 233, 0.3)"
//               }}
//               transition={{ duration: 0.6, delay: index * 0.2 }}
//               viewport={{ once: true }}
//               className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl border border-sky-500/20 hover:border-sky-500/50 shadow-lg hover:shadow-sky-500/40 transition-all duration-300"
//               style={{ perspective: 1000 }}
//             >
//               <motion.div 
//                 animate={{ y: [0, -10, 0] }}
//                 transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
//                 className="flex justify-center mb-4"
//               >
//                 <div className="h-14 w-14 rounded-full bg-sky-500/20 flex items-center justify-center">
//                   {benefit.icon}
//                 </div>
//               </motion.div>
//               <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
//               <p className="text-gray-300 text-sm">{benefit.description}</p>
//             </motion.div>
//           ))}
//         </div>
//       </motion.div>

//       {/* Sub-Services Detail View */}
//       <motion.div
//         id="services-section"
//         ref={servicesSectionRef}
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         transition={{ duration: 0.5 }}
//         className="py-20 bg-gradient-to-b from-black to-gray-900"
//       >
//         <div className="container mx-auto px-4">
//           {/* Service Selector */}
//           <motion.div
//             initial={{ opacity: 0, y: 50 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.7 }}
//             className="mb-12"
//           >
//             <h3 className="text-3xl font-bold text-white mb-6">Our Services</h3>
//             <div className="flex flex-wrap gap-4">
//               {services.map((service, index) => (
//                 <motion.button
//                   key={service.id}
//                   initial={{ opacity: 0, x: -50 }}
//                   animate={{ opacity: 1, x: 0 }}
//                   transition={{ duration: 0.5, delay: index * 0.1 }}
//                   whileHover={{ scale: 1.1, rotate: 3, boxShadow: "0 5px 15px rgba(14, 165, 233, 0.4)" }}
//                   whileTap={{ scale: 0.95 }}
//                   onClick={() => handleViewService(service)}
//                   className={`flex items-center px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 ${
//                     viewingService?.id.toString() === service.id.toString()
//                       ? 'bg-sky-500 text-black'
//                       : 'bg-white/10 text-white hover:bg-sky-500/80 hover:text-black'
//                   } backdrop-blur-sm border border-sky-500/20 hover:border-sky-500`}
//                 >
//                   <img
//                     src={service.icon}
//                     alt={service.title}
//                     className="h-6 w-6 mr-2 object-contain"
//                     onError={(e) => {
//                       e.target.onerror = null;
//                       e.target.src = "/assets/icons/default-service.svg";
//                     }}
//                   />
//                   {service.title}
//                 </motion.button>
//               ))}
//             </div>
//           </motion.div>

//           <motion.div 
//             initial={{ opacity: 0, y: 50 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.5 }}
//             className="mb-12"
//           >
//             <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
//               <div>
//                 <h2 className="text-5xl font-extrabold text-sky-500">{viewingService.title}</h2>
//                 <p className="text-gray-300 mt-3 max-w-2xl text-lg">{viewingService.description}</p>
//               </div>
              
//               <motion.a
//                 href="#contact"
//                 whileHover={{ scale: 1.05, boxShadow: "0 5px 15px rgba(14, 165, 233, 0.4)" }}
//                 whileTap={{ scale: 0.95 }}
//                 className="bg-sky-500 text-black py-3 px-8 rounded-lg hover:bg-sky-600 transition font-semibold shadow-lg hover:shadow-sky-500/50"
//               >
//                 Request a Quote <ArrowRight className="ml-2 h-5 w-5 inline" />
//               </motion.a>
//             </div>
//           </motion.div>
          
//           <div className="mb-16">
//             <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
//               {viewingService.subServices.map((subService, index) => (
//                 <motion.div
//                   key={subService.id}
//                   initial={{ opacity: 0, y: 100, rotateX: -30 }}
//                   animate={{ opacity: 1, y: 0, rotateX: 0 }}
//                   whileHover={{ 
//                     y: -10, 
//                     rotateX: 15, 
//                     rotateY: 15, 
//                     scale: 1.05,
//                     boxShadow: "0 15px 30px rgba(14, 165, 233, 0.3)"
//                   }}
//                   transition={{ duration: 0.6, delay: index * 0.2 }}
//                   className={`bg-white/5 backdrop-blur-lg rounded-2xl border ${
//                     activeSubService === subService.id 
//                       ? 'border-sky-500' 
//                       : 'border-sky-500/20 hover:border-sky-500/50'
//                   } shadow-lg hover:shadow-sky-500/40 transition-all duration-300`}
//                   style={{ perspective: 1000 }}
//                 >
//                   <div className="p-6 text-center">
//                     <motion.div 
//                       animate={{ y: [0, -10, 0] }}
//                       transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
//                       className="flex justify-center mb-4"
//                     >
//                       {getSubServiceIcon(subService)}
//                     </motion.div>
//                     <h3 className="text-xl font-bold text-white mb-3">{subService.title}</h3>
//                     <p className="text-gray-300 mb-4 line-clamp-3 text-sm">{subService.description}</p>
//                     <motion.button
//                       onClick={() => setActiveSubService(activeSubService === subService.id ? null : subService.id)}
//                       whileHover={{ scale: 1.1, boxShadow: "0 5px 15px rgba(14, 165, 233, 0.4)" }}
//                       whileTap={{ scale: 0.95 }}
//                       className="bg-sky-500 text-black px-6 py-2 rounded-lg hover:bg-sky-600 transition font-semibold"
//                     >
//                       Read More
//                     </motion.button>
//                   </div>
//                 </motion.div>
//               ))}
//             </div>
//           </div>
          
//           {/* Expanded Sub-Service Details */}
//           <AnimatePresence>
//             {activeSubService && viewingService.subServices.find(s => s.id === activeSubService) && (
//               <motion.div
//                 ref={subServiceDetailsRef}
//                 initial={{ opacity: 0, scale: 0.8 }}
//                 animate={{ opacity: 1, scale: 1 }}
//                 exit={{ opacity: 0, scale: 0.8 }}
//                 transition={{ duration: 0.5 }}
//                 className="bg-white/10 backdrop-blur-lg rounded-2xl border border-sky-500/50 shadow-2xl hover:shadow-sky-500/50 mb-16"
//               >
//                 {(() => {
//                   const subService = viewingService.subServices.find(s => s.id === activeSubService);
//                   return (
//                     <div className="grid grid-cols-1 lg:grid-cols-2">
//                       <motion.div 
//                         initial={{ opacity: 0, x: -100 }}
//                         animate={{ opacity: 1, x: 0 }}
//                         transition={{ duration: 0.5 }}
//                         className="p-8"
//                       >
//                         <div className="flex justify-between items-center mb-6">
//                           <h3 className="text-3xl font-bold text-white">{subService.title}</h3>
//                           <motion.button 
//                             onClick={() => setActiveSubService(null)}
//                             whileHover={{ scale: 1.2, rotate: 90 }}
//                             whileTap={{ scale: 0.9 }}
//                             className="bg-sky-500/20 rounded-full p-2 hover:bg-sky-500/40 transition"
//                           >
//                             <X className="h-5 w-5 text-sky-500" />
//                           </motion.button>
//                         </div>
                        
//                         <motion.div 
//                           initial={{ scale: 0.8 }}
//                           animate={{ scale: 1 }}
//                           transition={{ duration: 0.5 }}
//                           className="mb-6"
//                         >
//                           <img 
//                             src={subService.image || '/assets/images/default-subservice.jpg'} 
//                             alt={subService.title}
//                             className="w-full h-64 object-cover rounded-lg border border-sky-500/30"
//                             onError={(e) => {
//                               e.target.onerror = null;
//                               e.target.src = "/assets/images/default-subservice.jpg";
//                             }}
//                           />
//                         </motion.div>
                        
//                         <p className="text-gray-300 mb-8 leading-relaxed">
//                           {subService.description}
//                         </p>
                        
//                         <div className="space-y-8">
//                           <div>
//                             <h4 className="text-lg font-semibold text-sky-500 mb-4 flex items-center">
//                               <Award className="h-5 w-5 mr-2" /> 
//                               Key Benefits
//                             </h4>
//                             <ul className="space-y-3">
//                               {subService.benefits.map((benefit, i) => (
//                                 <motion.li 
//                                   key={i}
//                                   initial={{ opacity: 0, x: -20 }}
//                                   animate={{ opacity: 1, x: 0 }}
//                                   transition={{ delay: i * 0.1, duration: 0.3 }}
//                                   className="flex items-start"
//                                 >
//                                   <div className="bg-sky-500/20 p-1 rounded-full mr-3 flex-shrink-0">
//                                     <Check className="h-4 w-4 text-sky-500" />
//                                   </div>
//                                   <span className="text-gray-300">{benefit}</span>
//                                 </motion.li>
//                               ))}
//                             </ul>
//                           </div>

//                           <div>
//                             <h4 className="text-lg font-semibold text-sky-500 mb-4 flex items-center">
//                               <Sparkles className="h-5 w-5 mr-2" /> 
//                               Our Process
//                             </h4>
//                             <ul className="space-y-4">
//                               {subService.process.map((step, i) => (
//                                 <motion.li 
//                                   key={i}
//                                   initial={{ opacity: 0, x: -20 }}
//                                   animate={{ opacity: 1, x: 0 }}
//                                   transition={{ delay: i * 0.15, duration: 0.3 }}
//                                   className="flex items-start"
//                                 >
//                                   <div className="bg-sky-500 text-black rounded-full h-6 w-6 flex items-center justify-center mr-3 flex-shrink-0 font-bold text-sm">
//                                     {i + 1}
//                                   </div>
//                                   <span className="text-gray-300">{step}</span>
//                                 </motion.li>
//                               ))}
//                             </ul>
//                           </div>
                          
//                           <motion.div
//                             whileHover={{ scale: 1.05, boxShadow: "0 5px 15px rgba(14, 165, 233, 0.4)" }}
//                             whileTap={{ scale: 0.95 }}
//                           >
//                             <a
//                               href={`/request-consultation?service=${subService.id}`}
//                               className="inline-flex items-center bg-sky-500 text-black px-6 py-3 rounded-lg hover:bg-sky-600 transition shadow-lg hover:shadow-sky-500/50"
//                             >
//                               Request This Service <ArrowRight className="ml-2 h-5 w-5" />
//                             </a>
//                           </motion.div>
//                         </div>
//                       </motion.div>
                      
//                       <motion.div 
//                         initial={{ opacity: 0, x: 100 }}
//                         animate={{ opacity: 1, x: 0 }}
//                         transition={{ duration: 0.5 }}
//                         className="bg-white/5 backdrop-blur-lg p-8 flex flex-col"
//                       >
//                         <div className="mb-6">
//                           <h4 className="text-lg font-semibold text-sky-500 mb-4">Quick Information</h4>
//                           <motion.div 
//                             initial={{ y: 20, opacity: 0 }}
//                             animate={{ y: 0, opacity: 1 }}
//                             transition={{ duration: 0.5 }}
//                             className="bg-white/10 p-6 rounded-lg shadow-sm space-y-4 border border-sky-500/20"
//                           >
//                             <div className="flex items-center">
//                               <Clock className="h-5 w-5 text-sky-500 mr-3" />
//                               <div>
//                                 <span className="block text-sm text-gray-400">Average Turnaround</span>
//                                 <span className="text-white font-medium">2-3 Weeks</span>
//                               </div>
//                             </div>
                            
//                             <div className="flex items-center">
//                               <Calendar className="h-5 w-5 text-sky-500 mr-3" />
//                               <div>
//                                 <span className="block text-sm text-gray-400">Project Duration</span>
//                                 <span className="text-white font-medium">Depends on Scope</span>
//                               </div>
//                             </div>
                            
//                             <div className="flex items-center">
//                               <Award className="h-5 w-5 text-sky-500 mr-3" />
//                               <div>
//                                 <span className="block text-sm text-gray-400">Certifications</span>
//                                 <span className="text-white font-medium">Industry Standard Compliant</span>
//                               </div>
//                             </div>
//                           </motion.div>
//                         </div>
                        
//                         <div className="mb-8">
//                           <h4 className="text-lg font-semibold text-sky-500 mb-4">Contact Specialist</h4>
//                           <motion.div 
//                             initial={{ y: 20, opacity: 0 }}
//                             animate={{ y: 0, opacity: 1 }}
//                             transition={{ duration: 0.5 }}
//                             className="bg-white/10 p-6 rounded-lg shadow-sm border border-sky-500/20"
//                           >
//                             <div className="flex items-start mb-4">
//                               <Mail className="h-5 w-5 text-sky-500 mr-3 mt-0.5" />
//                               <a href="mailto:specialist@structuralsolutions.com" className="text-gray-300 hover:text-sky-500 transition">
//                                 specialist@structuralsolutions.com
//                               </a>
//                             </div>
//                             <div className="flex items-start">
//                               <Phone className="h-5 w-5 text-sky-500 mr-3 mt-0.5" />
//                               <a href="tel:+15551234567" className="text-gray-300 hover:text-sky-500 transition">
//                                 (555) 123-4567
//                               </a>
//                             </div>
//                           </motion.div>
//                         </div>
                        
//                         <div className="mt-auto">
//                           <motion.div 
//                             initial={{ y: 20, opacity: 0 }}
//                             animate={{ y: 0, opacity: 1 }}
//                             transition={{ duration: 0.5 }}
//                             className="bg-sky-500 rounded-lg p-6 text-black"
//                           >
//                             <h4 className="text-lg font-semibold flex items-center mb-3">
//                               <Play className="h-5 w-5 mr-2" />
//                               How It Works
//                             </h4>
//                             <p className="text-gray-900 mb-4">
//                               Schedule a no-obligation consultation to discuss your project requirements and receive a customized solution.
//                             </p>
//                             <motion.a
//                               href="#consultation"
//                               whileHover={{ scale: 1.05, boxShadow: "0 5px 15px rgba(0, 0, 0, 0.2)" }}
//                               whileTap={{ scale: 0.95 }}
//                               className="inline-block w-full text-center bg-black text-white py-3 px-6 rounded-lg font-semibold hover:bg-sky-600 transition"
//                             >
//                               Book a Consultation
//                             </motion.a>
//                           </motion.div>
//                         </div>
//                       </motion.div>
//                     </div>
//                   );
//                 })()}
//               </motion.div>
//             )}
//           </AnimatePresence>
          
//           {/* Call to Action */}
//           <motion.div
//             initial={{ opacity: 0, y: 100 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.8 }}
//             className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 md:p-12 text-white relative overflow-hidden border border-sky-500/50"
//           >
//             <motion.div 
//               animate={{ scale: [1, 1.02, 1] }}
//               transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
//               className="absolute inset-0 bg-gradient-to-r from-sky-500/20 to-transparent opacity-50"
//             ></motion.div>
//             <div className="relative z-10 text-center md:flex md:items-center md:justify-between">
//               <div className="mb-6 md:mb-0">
//                 <motion.h3 
//                   initial={{ y: 20, opacity: 0 }}
//                   animate={{ y: 0, opacity: 1 }}
//                   transition={{ duration: 0.6 }}
//                   className="text-4xl font-bold mb-3"
//                 >
//                   Ready to Start Your Project?
//                 </motion.h3>
//                 <motion.p 
//                   initial={{ y: 20, opacity: 0 }}
//                   animate={{ y: 0, opacity: 1 }}
//                   transition={{ duration: 0.6, delay: 0.2 }}
//                   className="text-gray-300 max-w-xl"
//                 >
//                   Our team of expert engineers is ready to help you bring your vision to life with precision and excellence.
//                 </motion.p>
//               </div>
//               <motion.div
//                 whileHover={{ scale: 1.05, boxShadow: "0 5px 15px rgba(14, 165, 233, 0.4)" }}
//                 whileTap={{ scale: 0.95 }}
//               >
//                 <a
//                   href="#contact"
//                   className="inline-flex items-center bg-sky-500 text-black px-8 py-4 rounded-lg font-semibold hover:bg-sky-600 transition shadow-lg hover:shadow-sky-500/50"
//                 >
//                   Schedule a Consultation <ArrowRight className="ml-2 h-5 w-5" />
//                 </a>
//               </motion.div>
//             </div>
//           </motion.div>
//         </div>
//       </motion.div>
//     </div>
//   );
// }

// export default Services;





// import { services } from '../data';
// import { useState, useEffect, useRef } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import { useLocation, useNavigate } from 'react-router-dom';
// import { 
//   ChevronRight, 
//   Check, 
//   ArrowRight, 
//   ChevronDown, 
//   Shield, 
//   Clock, 
//   Award, 
//   Zap, 
//   X, 
//   Sparkles,
//   Play,
//   Calendar,
//   Mail,
//   Phone,
//   Wrench,
//   Wind,
//   ThermometerSun,
//   SquareSigma,
//   BrickWall,
//   Blinds,
//   DraftingCompass,
//   ConstructionIcon,
//   Trees,
//   GlassWater,
//   Home,
//   ShieldCheck,
//   MapPin
// } from 'lucide-react';

// function Services() {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const [viewingService, setViewingService] = useState(null);
//   const [activeSubService, setActiveSubService] = useState(null);
//   const subServiceDetailsRef = useRef(null);
//   const servicesSectionRef = useRef(null);

//   // Set initial service with error handling
//   useEffect(() => {
//     console.log('Services data:', services);
//     const serviceId = location.state?.serviceId?.toString();
//     console.log('Service ID from state:', serviceId);
//     console.log('Location state:', location.state);

//     try {
//       if (!services || !Array.isArray(services) || services.length === 0) {
//         console.error('Services data is invalid or empty');
//         setViewingService({
//           id: 'fallback',
//           title: 'Default Service',
//           description: 'No services available at the moment.',
//           subServices: []
//         });
//         return;
//       }

//       const selectedService = serviceId 
//         ? services.find((service) => service.id.toString() === serviceId) 
//         : null;
//       const initialService = selectedService || services[0];
      
//       console.log('Selected service:', initialService);
//       if (initialService) {
//         setViewingService(initialService);
//       } else {
//         console.error('No initial service found');
//         setViewingService({
//           id: 'fallback',
//           title: 'Default Service',
//           description: 'No services available.',
//           subServices: []
//         });
//       }
//     } catch (error) {
//       console.error('Error setting initial service:', error);
//       setViewingService({
//         id: 'fallback',
//         title: 'Default Service',
//         description: 'An error occurred while loading services.',
//         subServices: []
//       });
//     }
//   }, [location.state]);

//   // Handle auto-scroll to services section
//   useEffect(() => {
//     const serviceId = location.state?.serviceId?.toString();
    
//     if (serviceId && servicesSectionRef.current) {
//       console.log('Attempting to scroll to services section');
//       const scrollToSection = () => {
//         window.scrollTo({ top: 0, behavior: 'instant' });
//         console.log('Reset scroll to top');

//         if (servicesSectionRef.current) {
//           const rect = servicesSectionRef.current.getBoundingClientRect();
//           const offsetTop = window.pageYOffset + rect.top;
//           const headerOffset = 80;
//           const scrollPosition = offsetTop - headerOffset;

//           window.scrollTo({
//             top: scrollPosition,
//             behavior: 'smooth'
//           });
//           console.log('Scrolling to position:', scrollPosition);
//         } else {
//           console.log('Services section ref not found');
//         }
//       };

//       const timer = setTimeout(scrollToSection, 1000);
//       return () => clearTimeout(timer);
//     }
//   }, [location.state, viewingService]);

//   // Auto-scroll to expanded sub-service details
//   useEffect(() => {
//     if (activeSubService && subServiceDetailsRef.current) {
//       setTimeout(() => {
//         subServiceDetailsRef.current.scrollIntoView({ 
//           behavior: 'smooth', 
//           block: 'start' 
//         });
//       }, 500);
//     }
//   }, [activeSubService]);

//   const handleViewService = (service) => {
//     console.log('Handling view service:', service);
//     setViewingService(service);
//     setActiveSubService(null);
//     navigate('/services', { state: { serviceId: service.id.toString() } });
//   };

//   const benefitItems = [
//     {
//       icon: <Shield className="h-8 w-8 text-sky-500" />,
//       title: "Certified Quality",
//       description: "All our engineering services meet rigorous industry standards and certifications."
//     },
//     {
//       icon: <Clock className="h-8 w-8 text-sky-500" />,
//       title: "Rapid Turnaround",
//       description: "Efficient processes ensure your projects stay on schedule and within budget."
//     },
//     {
//       icon: <Award className="h-8 w-8 text-sky-500" />,
//       title: "Expert Team",
//       description: "Our engineers bring decades of combined experience to every project."
//     },
//     {
//       icon: <Zap className="h-8 w-8 text-sky-500" />,
//       title: "Innovative Solutions",
//       description: "We employ cutting-edge technology and methods for optimal results."
//     }
//   ];

//   const getSubServiceIcon = (subService) => {
//     const id = subService.id;

//     switch (id) {
//       case 701:
//         return <Wind className="h-10 w-10 text-sky-500" />;
//       case 702:
//         return <ThermometerSun className="h-10 w-10 text-sky-500" />;
//       case 703:
//         return <SquareSigma className="h-10 w-10 text-sky-500" />;
//       case 704:
//         return <Wrench className="h-10 w-10 text-sky-500" />;
//       case 705:
//         return <BrickWall className="h-10 w-10 text-sky-500" />;
//       case 706:
//         return <Blinds className="h-10 w-10 text-sky-500" />;
//       case 707:
//         return <DraftingCompass className="h-10 w-10 text-sky-500" />;
//       case 801:
//         return <ConstructionIcon className="h-10 w-10 text-sky-500" />;
//       case 802:
//         return <Wrench className="h-10 w-10 text-sky-500" />;
//       case 803:
//         return <Trees className="h-10 w-10 text-sky-500" />;
//       case 804:
//       case 805:
//       case 806:
//         return <GlassWater className="h-10 w-10 text-sky-500" />;
//       case 807:
//         return <DraftingCompass className="h-10 w-10 text-sky-500" />;
//       case 901:
//         return <Wind className="h-10 w-10 text-sky-500" />;
//       case 902:
//         return <Home className="h-10 w-10 text-sky-500" />;
//       case 903:
//         return <Wrench className="h-10 w-10 text-sky-500" />;
//       case 904:
//         return <ShieldCheck className="h-10 w-10 text-sky-500" />;
//       case 905:
//         return <MapPin className="h-10 w-10 text-sky-500" />;
//       default:
//         return <Wrench className="h-10 w-10 text-sky-500" />;
//     }
//   };

//   if (!viewingService) {
//     return (
//       <div className="flex justify-center items-center min-h-screen bg-gradient-to-b from-sky-900 to-gray-800">
//         <motion.div
//           animate={{ rotate: 360 }}
//           transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
//           className="h-12 w-12 border-4 border-t-sky-500 border-white/30 rounded-full"
//         />
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen text-white font-sans bg-gradient-to-b from-sky-900 to-gray-800">
//       {/* Hero Section (Unchanged) */}
//       <div className="relative overflow-hidden bg-dark-blue text-white">
//         <div className="absolute inset-0 bg-gradient-to-r from-sky-900/60 to-transparent"></div>
//         <div 
//           className="absolute inset-0 bg-cover bg-center opacity-80" 
//           style={{ backgroundImage: `url('/assets/images/ESW/esw-westwharf-1.jpg')` }}
//         ></div>
        
//         <motion.div 
//           initial={{ opacity: 0, y: 20 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.8 }}
//           className="container mx-auto px-4 py-24 relative z-20 mt-20"
//         >
//           <div className="flex flex-col items-center text-center">
//             <motion.div
//               initial={{ scale: 0 }}
//               animate={{ scale: 1 }}
//               transition={{ delay: 0.2, duration: 0.5 }}
//               className="h-20 w-20 bg-sky-500 rounded-full flex items-center justify-center mb-6"
//             >
//               <motion.div 
//                 animate={{ rotate: 360 }} 
//                 transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
//                 className="h-12 w-12 bg-white/20 rounded-full"
//               ></motion.div>
//             </motion.div>

//             <motion.span 
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ delay: 0.4, duration: 0.6 }}
//               className="inline-block px-4 py-1 bg-sky-blue/30 text-white font-medium rounded-full mb-4"
//             >
//               Engineering Excellence
//             </motion.span>
            
//             <motion.h1 
//               initial={{ opacity: 0, y: 30 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: 0.5, duration: 0.7 }}
//               className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
//             >
//               The <span className="text-sky-blue">Facade Connection</span> Services
//             </motion.h1>
            
//             <motion.p 
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ delay: 0.7, duration: 0.7 }}
//               className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto"
//             >
//               Delivering precision-engineered solutions for the most demanding projects. Our innovative approach combines technical expertise with practical implementation for exceptional results.
//             </motion.p>
            
//             <motion.div
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: 0.9, duration: 0.7 }}
//               className="mt-10"
//             >
//               <a
//                 href="#services-section"
//                 className="bg-sky-blue text-white py-4 px-8 rounded-full hover:bg-sky-blue/90 transition duration-300 font-medium text-lg inline-flex items-center"
//               >
//                 Explore Our Services <ChevronDown className="ml-2 h-5 w-5" />
//               </a>
//             </motion.div>
//           </div>
//         </motion.div>
//       </div>

//       {/* Benefits Section */}
//       <motion.div 
//         initial={{ opacity: 0 }}
//         whileInView={{ opacity: 1 }}
//         transition={{ duration: 0.8 }}
//         viewport={{ once: true }}
//         className="container mx-auto px-4 py-20"
//       >
//         <div className="text-center mb-12">
//           <motion.h2 
//             initial={{ y: 50, opacity: 0 }}
//             whileInView={{ y: 0, opacity: 1 }}
//             transition={{ duration: 0.6 }}
//             className="text-5xl font-extrabold text-sky-500 mb-4 tracking-tight"
//           >
//             Why Choose Us
//           </motion.h2>
//           <motion.p 
//             initial={{ y: 30, opacity: 0 }}
//             whileInView={{ y: 0, opacity: 1 }}
//             transition={{ duration: 0.6, delay: 0.2 }}
//             className="text-gray-200 max-w-2xl mx-auto text-lg"
//           >
//             Unmatched expertise and innovation for projects that demand excellence.
//           </motion.p>
//         </div>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
//           {benefitItems.map((benefit, index) => (
//             <motion.div
//               key={index}
//               initial={{ opacity: 0, y: 100, rotateX: -45 }}
//               whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
//               whileHover={{ 
//                 y: -15, 
//                 rotateX: 15, 
//                 rotateY: 15, 
//                 scale: 1.1,
//                 boxShadow: "0 20px 40px rgba(14, 165, 233, 0.4)"
//               }}
//               transition={{ duration: 0.7, delay: index * 0.2 }}
//               viewport={{ once: true }}
//               className="bg-white/10 backdrop-blur-xl p-6 rounded-2xl border border-sky-500/30 hover:border-sky-500 shadow-xl hover:shadow-sky-500/50 transition-all duration-300"
//               style={{ perspective: 1200 }}
//             >
//               <motion.div 
//                 animate={{ y: [0, -15, 0] }}
//                 transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
//                 className="flex justify-center mb-4"
//               >
//                 <div className="h-16 w-16 rounded-full bg-sky-500/30 flex items-center justify-center border border-sky-500/50">
//                   {benefit.icon}
//                 </div>
//               </motion.div>
//               <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
//               <p className="text-gray-200 text-sm">{benefit.description}</p>
//             </motion.div>
//           ))}
//         </div>
//       </motion.div>

//       {/* Sub-Services Detail View */}
//       <motion.div
//         id="services-section"
//         ref={servicesSectionRef}
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         transition={{ duration: 0.5 }}
//         className="py-20 bg-gradient-to-b from-gray-800 to-sky-900"
//       >
//         <div className="container mx-auto px-4">
//           {/* Service Selector */}
//           <motion.div
//             initial={{ opacity: 0, y: 50 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.7 }}
//             className="mb-12"
//           >
//             <h3 className="text-3xl font-bold text-white mb-6">Our Services</h3>
//             <div className="flex flex-wrap gap-4">
//               {services.map((service, index) => (
//                 <motion.button
//                   key={service.id}
//                   initial={{ opacity: 0, x: -50 }}
//                   animate={{ opacity: 1, x: 0 }}
//                   transition={{ duration: 0.5, delay: index * 0.1 }}
//                   whileHover={{ 
//                     scale: 1.15, 
//                     rotate: 5, 
//                     boxShadow: "0 8px 20px rgba(14, 165, 233, 0.5)" 
//                   }}
//                   whileTap={{ scale: 0.9 }}
//                   onClick={() => handleViewService(service)}
//                   className={`flex items-center px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
//                     viewingService?.id.toString() === service.id.toString()
//                       ? 'bg-sky-500 text-black'
//                       : 'bg-white/10 text-white hover:bg-sky-500/90 hover:text-black'
//                   } backdrop-blur-sm border border-sky-500/30 hover:border-sky-500/70`}
//                 >
//                   <img
//                     src={service.icon}
//                     alt={service.title}
//                     className="h-6 w-6 mr-2 object-contain"
//                     onError={(e) => {
//                       e.target.onerror = null;
//                       e.target.src = "/assets/icons/default-service.svg";
//                     }}
//                   />
//                   {service.title}
//                 </motion.button>
//               ))}
//             </div>
//           </motion.div>

//           <motion.div 
//             initial={{ opacity: 0, y: 50 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.5 }}
//             className="mb-12"
//           >
//             <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
//               <div>
//                 <h2 className="text-5xl font-extrabold text-sky-500">{viewingService.title}</h2>
//                 <p className="text-gray-200 mt-3 max-w-2xl text-lg">{viewingService.description}</p>
//               </div>
              
//               <motion.a
//                 href="#contact"
//                 whileHover={{ 
//                   scale: 1.1, 
//                   boxShadow: "0 8px 20px rgba(14, 165, 233, 0.5)" 
//                 }}
//                 whileTap={{ scale: 0.95 }}
//                 className="bg-sky-500 text-black py-3 px-8 rounded-xl hover:bg-sky-600 transition font-semibold shadow-lg hover:shadow-sky-500/60"
//               >
//                 Request a Quote <ArrowRight className="ml-2 h-5 w-5 inline" />
//               </motion.a>
//             </div>
//           </motion.div>
          
//           <div className="mb-16">
//             <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
//               {viewingService.subServices.map((subService, index) => (
//                 <motion.div
//                   key={subService.id}
//                   initial={{ opacity: 0, y: 100, rotateX: -45 }}
//                   animate={{ opacity: 1, y: 0, rotateX: 0 }}
//                   whileHover={{ 
//                     y: -15, 
//                     rotateX: 20, 
//                     rotateY: 20, 
//                     scale: 1.1,
//                     boxShadow: "0 20px 40px rgba(14, 165, 233, 0.4)"
//                   }}
//                   transition={{ duration: 0.7, delay: index * 0.2 }}
//                   className={`bg-white/10 backdrop-blur-xl rounded-2xl border ${
//                     activeSubService === subService.id 
//                       ? 'border-sky-500' 
//                       : 'border-sky-500/30 hover:border-sky-500/70'
//                   } shadow-xl hover:shadow-sky-500/60 transition-all duration-300`}
//                   style={{ perspective: 1200 }}
//                 >
//                   <div className="p-6 text-center">
//                     <motion.div 
//                       animate={{ y: [0, -15, 0] }}
//                       transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
//                       className="flex justify-center mb-4"
//                     >
//                       <div className="h-16 w-16 rounded-full bg-sky-500/30 flex items-center justify-center border border-sky-500/50">
//                         {getSubServiceIcon(subService)}
//                       </div>
//                     </motion.div>
//                     <h3 className="text-xl font-bold text-white mb-3">{subService.title}</h3>
//                     <p className="text-gray-200 mb-4 line-clamp-3 text-sm">{subService.description}</p>
//                     <motion.button
//                       onClick={() => setActiveSubService(activeSubService === subService.id ? null : subService.id)}
//                       whileHover={{ 
//                         scale: 1.15, 
//                         boxShadow: "0 8px 20px rgba(14, 165, 233, 0.5)" 
//                       }}
//                       whileTap={{ scale: 0.95 }}
//                       className="bg-sky-500 text-black px-6 py-2 rounded-xl hover:bg-sky-600 transition font-semibold"
//                     >
//                       Read More
//                     </motion.button>
//                   </div>
//                 </motion.div>
//               ))}
//             </div>
//           </div>
          
//           {/* Expanded Sub-Service Details */}
//           <AnimatePresence>
//             {activeSubService && viewingService.subServices.find(s => s.id === activeSubService) && (
//               <motion.div
//                 ref={subServiceDetailsRef}
//                 initial={{ opacity: 0, scale: 0.8 }}
//                 animate={{ opacity: 1, scale: 1 }}
//                 exit={{ opacity: 0, scale: 0.8 }}
//                 transition={{ duration: 0.5 }}
//                 className="bg-white/15 backdrop-blur-xl rounded-2xl border border-sky-500/50 shadow-2xl hover:shadow-sky-500/60 mb-16"
//               >
//                 {(() => {
//                   const subService = viewingService.subServices.find(s => s.id === activeSubService);
//                   return (
//                     <div className="grid grid-cols-1 lg:grid-cols-2">
//                       <motion.div 
//                         initial={{ opacity: 0, x: -100 }}
//                         animate={{ opacity: 1, x: 0 }}
//                         transition={{ duration: 0.5 }}
//                         className="p-8"
//                       >
//                         <div className="flex justify-between items-center mb-6">
//                           <h3 className="text-3xl font-bold text-white">{subService.title}</h3>
//                           <motion.button 
//                             onClick={() => setActiveSubService(null)}
//                             whileHover={{ scale: 1.3, rotate: 90 }}
//                             whileTap={{ scale: 0.9 }}
//                             className="bg-sky-500/30 rounded-full p-2 hover:bg-sky-500/50 transition"
//                           >
//                             <X className="h-5 w-5 text-sky-500" />
//                           </motion.button>
//                         </div>
                        
//                         <motion.div 
//                           initial={{ scale: 0.8 }}
//                           animate={{ scale: 1 }}
//                           transition={{ duration: 0.5 }}
//                           className="mb-6"
//                         >
//                           <img 
//                             src={subService.image || '/assets/images/default-subservice.jpg'} 
//                             alt={subService.title}
//                             className="w-full h-64 object-cover rounded-xl border border-sky-500/40"
//                             onError={(e) => {
//                               e.target.onerror = null;
//                               e.target.src = "/assets/images/default-subservice.jpg";
//                             }}
//                           />
//                         </motion.div>
                        
//                         <p className="text-gray-200 mb-8 leading-relaxed">
//                           {subService.description}
//                         </p>
                        
//                         <div className="space-y-8">
//                           <div>
//                             <h4 className="text-lg font-semibold text-sky-500 mb-4 flex items-center">
//                               <Award className="h-5 w-5 mr-2" /> 
//                               Key Benefits
//                             </h4>
//                             <ul className="space-y-3">
//                               {subService.benefits.map((benefit, i) => (
//                                 <motion.li 
//                                   key={i}
//                                   initial={{ opacity: 0, x: -20 }}
//                                   animate={{ opacity: 1, x: 0 }}
//                                   transition={{ delay: i * 0.1, duration: 0.3 }}
//                                   className="flex items-start"
//                                 >
//                                   <div className="bg-sky-500/30 p-1 rounded-full mr-3 flex-shrink-0">
//                                     <Check className="h-4 w-4 text-sky-500" />
//                                   </div>
//                                   <span className="text-gray-200">{benefit}</span>
//                                 </motion.li>
//                               ))}
//                             </ul>
//                           </div>

//                           <div>
//                             <h4 className="text-lg font-semibold text-sky-500 mb-4 flex items-center">
//                               <Sparkles className="h-5 w-5 mr-2" /> 
//                               Our Process
//                             </h4>
//                             <ul className="space-y-4">
//                               {subService.process.map((step, i) => (
//                                 <motion.li 
//                                   key={i}
//                                   initial={{ opacity: 0, x: -20 }}
//                                   animate={{ opacity: 1, x: 0 }}
//                                   transition={{ delay: i * 0.15, duration: 0.3 }}
//                                   className="flex items-start"
//                                 >
//                                   <div className="bg-sky-500 text-black rounded-full h-6 w-6 flex items-center justify-center mr-3 flex-shrink-0 font-bold text-sm">
//                                     {i + 1}
//                                   </div>
//                                   <span className="text-gray-200">{step}</span>
//                                 </motion.li>
//                               ))}
//                             </ul>
//                           </div>
                          
//                           <motion.div
//                             whileHover={{ 
//                               scale: 1.1, 
//                               boxShadow: "0 8px 20px rgba(14, 165, 233, 0.5)" 
//                             }}
//                             whileTap={{ scale: 0.95 }}
//                           >
//                             <a
//                               href={`/request-consultation?service=${subService.id}`}
//                               className="inline-flex items-center bg-sky-500 text-black px-6 py-3 rounded-xl hover:bg-sky-600 transition shadow-lg hover:shadow-sky-500/60"
//                             >
//                               Request This Service <ArrowRight className="ml-2 h-5 w-5" />
//                             </a>
//                           </motion.div>
//                         </div>
//                       </motion.div>
                      
//                       <motion.div 
//                         initial={{ opacity: 0, x: 100 }}
//                         animate={{ opacity: 1, x: 0 }}
//                         transition={{ duration: 0.5 }}
//                         className="bg-white/5 backdrop-blur-xl p-8 flex flex-col"
//                       >
//                         <div className="mb-6">
//                           <h4 className="text-lg font-semibold text-sky-500 mb-4">Quick Information</h4>
//                           <motion.div 
//                             initial={{ y: 20, opacity: 0 }}
//                             animate={{ y: 0, opacity: 1 }}
//                             transition={{ duration: 0.5 }}
//                             className="bg-white/15 p-6 rounded-xl shadow-sm space-y-4 border border-sky-500/40"
//                           >
//                             <div className="flex items-center">
//                               <Clock className="h-5 w-5 text-sky-500 mr-3" />
//                               <div>
//                                 <span className="block text-sm text-gray-300">Average Turnaround</span>
//                                 <span className="text-white font-medium">2-3 Weeks</span>
//                               </div>
//                             </div>
                            
//                             <div className="flex items-center">
//                               <Calendar className="h-5 w-5 text-sky-500 mr-3" />
//                               <div>
//                                 <span className="block text-sm text-gray-300">Project Duration</span>
//                                 <span className="text-white font-medium">Depends on Scope</span>
//                               </div>
//                             </div>
                            
//                             <div className="flex items-center">
//                               <Award className="h-5 w-5 text-sky-500 mr-3" />
//                               <div>
//                                 <span className="block text-sm text-gray-300">Certifications</span>
//                                 <span className="text-white font-medium">Industry Standard Compliant</span>
//                               </div>
//                             </div>
//                           </motion.div>
//                         </div>
                        
//                         <div className="mb-8">
//                           <h4 className="text-lg font-semibold text-sky-500 mb-4">Contact Specialist</h4>
//                           <motion.div 
//                             initial={{ y: 20, opacity: 0 }}
//                             animate={{ y: 0, opacity: 1 }}
//                             transition={{ duration: 0.5 }}
//                             className="bg-white/15 p-6 rounded-xl shadow-sm border border-sky-500/40"
//                           >
//                             <div className="flex items-start mb-4">
//                               <Mail className="h-5 w-5 text-sky-500 mr-3 mt-0.5" />
//                               <a href="mailto:specialist@structuralsolutions.com" className="text-gray-200 hover:text-sky-500 transition">
//                                 specialist@structuralsolutions.com
//                               </a>
//                             </div>
//                             <div className="flex items-start">
//                               <Phone className="h-5 w-5 text-sky-500 mr-3 mt-0.5" />
//                               <a href="tel:+15551234567" className="text-gray-200 hover:text-sky-500 transition">
//                                 (555) 123-4567
//                               </a>
//                             </div>
//                           </motion.div>
//                         </div>
                        
//                         <div className="mt-auto">
//                           <motion.div 
//                             initial={{ y: 20, opacity: 0 }}
//                             animate={{ y: 0, opacity: 1 }}
//                             transition={{ duration: 0.5 }}
//                             className="bg-sky-500 rounded-xl p-6 text-black"
//                           >
//                             <h4 className="text-lg font-semibold flex items-center mb-3">
//                               <Play className="h-5 w-5 mr-2" />
//                               How It Works
//                             </h4>
//                             <p className="text-gray-900 mb-4">
//                               Schedule a no-obligation consultation to discuss your project requirements and receive a customized solution.
//                             </p>
//                             <motion.a
//                               href="#consultation"
//                               whileHover={{ scale: 1.05, boxShadow: "0 8px 20px rgba(0, 0, 0, 0.3)" }}
//                               whileTap={{ scale: 0.95 }}
//                               className="inline-block w-full text-center bg-black text-white py-3 px-6 rounded-xl font-semibold hover:bg-sky-600 transition"
//                             >
//                               Book a Consultation
//                             </motion.a>
//                           </motion.div>
//                         </div>
//                       </motion.div>
//                     </div>
//                   );
//                 })()}
//               </motion.div>
//             )}
//           </AnimatePresence>
          
//           {/* Call to Action */}
//           <motion.div
//             initial={{ opacity: 0, y: 100 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.8 }}
//             className="bg-white/15 backdrop-blur-xl rounded-2xl p-8 md:p-12 text-white relative overflow-hidden border border-sky-500/50"
//           >
//             <motion.div 
//               animate={{ scale: [1, 1.03, 1] }}
//               transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
//               className="absolute inset-0 bg-gradient-to-r from-sky-500/30 to-transparent opacity-50"
//             ></motion.div>
//             <div className="relative z-10 text-center md:flex md:items-center md:justify-between">
//               <div className="mb-6 md:mb-0">
//                 <motion.h3 
//                   initial={{ y: 20, opacity: 0 }}
//                   animate={{ y: 0, opacity: 1 }}
//                   transition={{ duration: 0.6 }}
//                   className="text-4xl font-bold mb-3 text-sky-500"
//                 >
//                   Ready to Start Your Project?
//                 </motion.h3>
//                 <motion.p 
//                   initial={{ y: 20, opacity: 0 }}
//                   animate={{ y: 0, opacity: 1 }}
//                   transition={{ duration: 0.6, delay: 0.2 }}
//                   className="text-gray-200 max-w-xl"
//                 >
//                   Our team of expert engineers is ready to help you bring your vision to life with precision and excellence.
//                 </motion.p>
//               </div>
//               <motion.div
//                 whileHover={{ scale: 1.1, boxShadow: "0 8px 20px rgba(14, 165, 233, 0.5)" }}
//                 whileTap={{ scale: 0.95 }}
//               >
//                 <a
//                   href="#contact"
//                   className="inline-flex items-center bg-sky-500 text-black px-8 py-4 rounded-xl font-semibold hover:bg-sky-600 transition shadow-lg hover:shadow-sky-500/60"
//                 >
//                   Schedule a Consultation <ArrowRight className="ml-2 h-5 w-5" />
//                 </a>
//               </motion.div>
//             </div>
//           </motion.div>
//         </div>
//       </motion.div>
//     </div>
//   );
// }

// export default Services;
